import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Box, Button, TextField, MenuItem, InputLabel, FormControl,
  InputAdornment, IconButton, Select, Typography,
  FormHelperText, Stack, Avatar, Tooltip, Paper,
  FormControlLabel, Checkbox, Divider,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText, Grid
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import axios from 'axios';
import config from '../config/config';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';

const elegantInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    transition: 'all 0.2s ease-in-out',
    fontSize: 'clamp(0.74rem, 0.7rem + 0.08vw, 0.82rem)',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#0655A5', borderWidth: '1.5px' },
    '&.Mui-focused': { backgroundColor: '#ffffff', boxShadow: '0 0 0 3px rgba(6,85,165,0.08)' },
  },
  '& .MuiInputLabel-root': { fontWeight: 700, fontSize: 'clamp(0.72rem, 0.68rem + 0.06vw, 0.8rem)', color: '#475569' },
  '& .MuiFormHelperText-root': { ml: 0, fontSize: 'clamp(0.64rem, 0.6rem + 0.04vw, 0.7rem)' },
};

const SectionCard = ({ icon, title, subtitle, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: 'clamp(10px, 1.2vw, 16px)',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      backgroundColor: '#ffffff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 'clamp(8px, 1vw, 12px)', pb: 'clamp(6px, 0.7vw, 8px)', borderBottom: '1px solid #f1f5f9' }}>
      <Avatar sx={{ width: 'clamp(24px, 2vw, 28px)', height: 'clamp(24px, 2vw, 28px)', bgcolor: 'rgba(6,85,165,0.08)', color: '#0655A5', borderRadius: '7px' }}>
        {React.cloneElement(icon, { sx: { fontSize: 'clamp(14px, 1.1vw, 17px)' } })}
      </Avatar>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: 'clamp(0.78rem, 0.75rem + 0.08vw, 0.88rem)', lineHeight: 1.2 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: '#64748b', fontSize: 'clamp(0.65rem, 0.62rem + 0.05vw, 0.72rem)' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    {children}
  </Paper>
);

/** Drawer selector (lista con scroll), sin dependencias extra */
/** Componente selector modal centrado (Opción 2) */
function SelectorModal({
  open,
  onClose,
  title,
  options = [],     // [{id, nombre, numeroCc?, proyecto?, division?}]
  selectedIds = [], // number[]
  onChange,         // (ids: number[]) => void
  container,
  isCentros = false,
}) {
  const [query, setQuery] = useState('');
  const [filtroProyecto, setFiltroProyecto] = useState([]);
  const [filtroDivision, setFiltroDivision] = useState([]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setFiltroProyecto([]);
      setFiltroDivision([]);
    }
  }, [open]);

  // Lista única de Proyectos y Divisiones disponibles en las opciones
  const proyectosDisponibles = useMemo(() => {
    if (!isCentros) return [];
    const set = new Set(options.map(o => o.proyecto).filter(Boolean));
    return Array.from(set).sort();
  }, [options, isCentros]);

  const divisionesDisponibles = useMemo(() => {
    if (!isCentros) return [];
    let opts = options;
    if (filtroProyecto.length > 0) {
      opts = opts.filter(o => filtroProyecto.includes(o.proyecto));
    }
    const set = new Set(opts.map(o => o.division).filter(Boolean));
    return Array.from(set).sort();
  }, [options, isCentros, filtroProyecto]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let result = options;

    if (isCentros) {
      if (filtroProyecto.length > 0) {
        result = result.filter(o => filtroProyecto.includes(o.proyecto));
      }
      if (filtroDivision.length > 0) {
        result = result.filter(o => filtroDivision.includes(o.division));
      }
    }

    if (q) {
      result = result.filter(o =>
        (o.nombre || '').toLowerCase().includes(q) ||
        (isCentros && (o.numeroCc || '').toLowerCase().includes(q)) ||
        (isCentros && (o.proyecto || '').toLowerCase().includes(q)) ||
        (isCentros && (o.division || '').toLowerCase().includes(q))
      );
    }

    // Ordenar centros por numeroCc (A→Z), luego por nombre
    if (isCentros) {
      result = [...result].sort((a, b) => {
        const na = (a.numeroCc || '').toLowerCase();
        const nb = (b.numeroCc || '').toLowerCase();
        if (na < nb) return -1;
        if (na > nb) return 1;
        return (a.nombre || '').localeCompare(b.nombre || '');
      });
    }
    return result;
  }, [options, query, isCentros, filtroProyecto, filtroDivision]);

  const toggleId = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every(o => selectedSet.has(o.id));
  const someFilteredSelected = filtered.some(o => selectedSet.has(o.id)) && !allFilteredSelected;

  const toggleFilteredBulk = (checked) => {
    const next = new Set(selectedSet);
    if (checked) {
      for (const o of filtered) next.add(o.id);
    } else {
      for (const o of filtered) next.delete(o.id);
    }
    onChange(Array.from(next));
  };

  const clearAll = () => {
    const next = new Set(selectedSet);
    for (const o of options) next.delete(o.id);
    onChange(Array.from(next));
  };

  const selectAll = () => {
    onChange(options.map(o => o.id));
  };

  const filteredSelectedCount = filtered.reduce((acc, o) => acc + (selectedSet.has(o.id) ? 1 : 0), 0);

  return (
    <Dialog
      open={open}
      disableRestoreFocus
      onClose={onClose}
      maxWidth="md"
      fullWidth
      container={container}
      disableEnforceFocus
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
          border: '1px solid #e2e8f0'
        }
      }}
    >
      {/* Header del Modal Centrado */}
      <Box
        sx={{
          px: 'clamp(12px, 1.5vw, 20px)',
          py: 'clamp(10px, 1.2vw, 14px)',
          background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 900, fontSize: 'clamp(0.85rem, 0.8rem + 0.2vw, 1rem)', lineHeight: 1.2 }} noWrap>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.88, fontSize: 'clamp(0.68rem, 0.65rem + 0.05vw, 0.75rem)' }}>
            {selectedIds.length} de {options.length} elementos seleccionados
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.8}>
          <Tooltip title="Seleccionar todo">
            <Button
              size="small"
              onClick={selectAll}
              startIcon={<SelectAllIcon sx={{ fontSize: 'clamp(12px, 1vw, 14px) !important' }} />}
              sx={{
                color: '#ffffff',
                bgcolor: 'rgba(255,255,255,0.15)',
                fontSize: 'clamp(0.65rem, 0.62rem + 0.05vw, 0.72rem)',
                fontWeight: 800,
                textTransform: 'none',
                py: 'clamp(2px, 0.3vw, 4px)',
                px: 'clamp(6px, 0.8vw, 10px)',
                borderRadius: '6px',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
              }}
            >
              Todo
            </Button>
          </Tooltip>
          <Tooltip title="Quitar selección">
            <Button
              size="small"
              onClick={clearAll}
              startIcon={<ClearAllIcon sx={{ fontSize: 'clamp(12px, 1vw, 14px) !important' }} />}
              sx={{
                color: '#ffffff',
                bgcolor: 'rgba(255,255,255,0.15)',
                fontSize: 'clamp(0.65rem, 0.62rem + 0.05vw, 0.72rem)',
                fontWeight: 800,
                textTransform: 'none',
                py: 'clamp(2px, 0.3vw, 4px)',
                px: 'clamp(6px, 0.8vw, 10px)',
                borderRadius: '6px',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
              }}
            >
              Limpiar
            </Button>
          </Tooltip>
        </Stack>

        <IconButton onClick={onClose} size="small" sx={{ color: 'white', ml: 0.5, p: '4px', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
          <CloseIcon fontSize="small" sx={{ fontSize: 'clamp(14px, 1.1vw, 18px)' }} />
        </IconButton>
      </Box>

      {/* Buscador + Filtros por Proyecto y División */}
      <Box sx={{ p: 'clamp(10px, 1.2vw, 16px)', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Stack spacing={1}>
          {isCentros && (proyectosDisponibles.length > 0 || divisionesDisponibles.length > 0) && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {proyectosDisponibles.length > 0 && (
                <Autocomplete
                  multiple
                  size="small"
                  options={proyectosDisponibles}
                  value={filtroProyecto}
                  onChange={(_, val) => {
                    setFiltroProyecto(val);
                  }}
                  limitTags={2}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option}
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                        sx={{ borderRadius: '5px', fontWeight: 600, fontSize: '0.66rem', height: 20 }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Filtrar Proyecto(s)"
                      placeholder={filtroProyecto.length ? '' : 'Todos los proyectos'}
                      sx={{
                        ...elegantInputSx,
                        bgcolor: '#ffffff',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-root': {
                          minHeight: 'clamp(30px, 2.6vw, 36px)',
                          fontSize: 'clamp(0.72rem, 0.68rem + 0.06vw, 0.8rem)'
                        }
                      }}
                    />
                  )}
                  sx={{ minWidth: 'clamp(180px, 20vw, 250px)', flex: 1 }}
                />
              )}

              {divisionesDisponibles.length > 0 && (
                <Autocomplete
                  multiple
                  size="small"
                  options={divisionesDisponibles}
                  value={filtroDivision}
                  onChange={(_, val) => setFiltroDivision(val)}
                  limitTags={2}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option}
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                        sx={{ borderRadius: '5px', fontWeight: 600, fontSize: '0.66rem', height: 20 }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Filtrar Grupo(s) / División"
                      placeholder={filtroDivision.length ? '' : 'Todas las divisiones'}
                      sx={{
                        ...elegantInputSx,
                        bgcolor: '#ffffff',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-root': {
                          minHeight: 'clamp(30px, 2.6vw, 36px)',
                          fontSize: 'clamp(0.72rem, 0.68rem + 0.06vw, 0.8rem)'
                        }
                      }}
                    />
                  )}
                  sx={{ minWidth: 'clamp(180px, 20vw, 250px)', flex: 1 }}
                />
              )}
            </Stack>
          )}

          <TextField
            size="small"
            fullWidth
            placeholder={isCentros ? 'Buscar por nombre, número, proyecto o división...' : 'Buscar...'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 17px)', color: '#64748b' }} />
                </InputAdornment>
              )
            }}
            sx={{
              ...elegantInputSx,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: '#ffffff',
                height: 'clamp(30px, 2.6vw, 36px)',
                fontSize: 'clamp(0.72rem, 0.68rem + 0.06vw, 0.8rem)'
              }
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={allFilteredSelected}
                  indeterminate={someFilteredSelected}
                  onChange={(e) => toggleFilteredBulk(e.target.checked)}
                  disabled={filtered.length === 0}
                  sx={{ p: 0.4, mr: 0.4, color: '#94a3b8', '&.Mui-checked': { color: '#0655A5' } }}
                />
              }
              label={
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <Typography variant="body2" sx={{ fontSize: 'clamp(0.72rem, 0.68rem + 0.06vw, 0.78rem)', fontWeight: 700, color: '#334155' }}>
                    {allFilteredSelected
                      ? 'Quitar selección (visibles)'
                      : someFilteredSelected
                        ? 'Completar selección (visibles)'
                        : 'Seleccionar visibles'}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${filteredSelectedCount}/${filtered.length}`}
                    sx={{
                      fontSize: 'clamp(0.6rem, 0.56rem + 0.05vw, 0.66rem)',
                      height: 18,
                      bgcolor: '#e2e8f0',
                      color: '#334155',
                      fontWeight: 800
                    }}
                  />
                </Stack>
              }
            />

            {(filtroProyecto.length > 0 || filtroDivision.length > 0 || query) && (
              <Button
                size="small"
                onClick={() => {
                  setFiltroProyecto([]);
                  setFiltroDivision([]);
                  setQuery('');
                }}
                sx={{ fontSize: 'clamp(0.68rem, 0.64rem + 0.05vw, 0.74rem)', textTransform: 'none', color: '#0655A5', fontWeight: 700 }}
              >
                Limpiar filtros
              </Button>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Lista desplazable */}
      <DialogContent sx={{ p: 'clamp(6px, 0.8vw, 10px)', maxHeight: 'clamp(260px, 40vh, 380px)', minHeight: 200, bgcolor: '#ffffff' }}>
        <List dense disablePadding>
          {filtered.map((item) => {
            const checked = selectedSet.has(item.id);
            return (
              <ListItem key={item.id} disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  onClick={() => toggleId(item.id)}
                  sx={{
                    borderRadius: '8px',
                    py: 'clamp(3px, 0.4vw, 5px)',
                    px: 'clamp(8px, 1vw, 12px)',
                    bgcolor: checked ? 'rgba(6,85,165,0.04)' : 'transparent',
                    border: checked ? '1px solid rgba(6,85,165,0.15)' : '1px solid transparent',
                    '&:hover': { bgcolor: checked ? 'rgba(6,85,165,0.08)' : '#f1f5f9' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={checked}
                      tabIndex={-1}
                      disableRipple
                      sx={{ p: 0, color: '#cbd5e1', '&.Mui-checked': { color: '#0655A5' } }}
                    />
                  </ListItemIcon>
                  {isCentros ? (
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.2, flexWrap: 'wrap' }}>
                          {item.numeroCc && (
                            <Typography variant="caption" sx={{ fontWeight: 800, fontSize: 'clamp(0.72rem, 0.68rem + 0.06vw, 0.78rem)', color: checked ? '#0655A5' : '#64748b', lineHeight: 1.2 }}>
                              {item.numeroCc}
                            </Typography>
                          )}
                          {item.division && (
                            <Chip
                              size="small"
                              label={item.division}
                              sx={{ fontSize: 'clamp(0.6rem, 0.56rem + 0.05vw, 0.66rem)', height: 18, bgcolor: checked ? 'rgba(6,85,165,0.12)' : '#f1f5f9', color: checked ? '#0655A5' : '#475569', fontWeight: 700 }}
                            />
                          )}
                          {item.proyecto && (
                            <Chip
                              size="small"
                              label={item.proyecto}
                              variant="outlined"
                              sx={{ fontSize: 'clamp(0.6rem, 0.56rem + 0.05vw, 0.66rem)', height: 18, borderColor: '#cbd5e1', color: '#64748b', fontWeight: 600 }}
                            />
                          )}
                        </Stack>
                      }
                      secondary={
                        <Typography variant="body2" noWrap sx={{ fontSize: 'clamp(0.74rem, 0.7rem + 0.06vw, 0.8rem)', fontWeight: checked ? 700 : 500, color: checked ? '#0655A5' : '#1e293b', lineHeight: 1.3 }} title={item.nombre}>
                          {item.nombre}
                        </Typography>
                      }
                      disableTypography
                    />
                  ) : (
                    <ListItemText
                      primaryTypographyProps={{
                        variant: 'body2',
                        noWrap: true,
                        fontSize: '0.825rem',
                        fontWeight: checked ? 700 : 500,
                        color: checked ? '#0655A5' : '#1e293b'
                      }}
                      primary={item.nombre}
                      title={item.nombre}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
          {filtered.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center', color: '#64748b' }}>
              <Typography variant="body2" fontSize="0.85rem">
                No se encontraron resultados{query ? ` para “${query}”` : ''}.
              </Typography>
            </Box>
          )}
        </List>
      </DialogContent>

      <Divider />

      {/* Footer del Modal Centrado */}
      <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#ffffff', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, fontSize: '0.78rem' }}>
          <strong>{selectedIds.length}</strong> seleccionados
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={onClose}
          sx={{
            borderRadius: '8px',
            px: 3,
            py: 0.6,
            fontWeight: 700,
            fontSize: '0.8rem',
            textTransform: 'none',
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            boxShadow: '0 3px 8px rgba(6,85,165,0.2)'
          }}
        >
          Listo
        </Button>
      </DialogActions>
    </Dialog>
  );
}


const ModalUsuarios = ({
  isOpen,
  handleClose,
  handleSave,
  formData,
  handleInputChange,
  dataEmpresa,
  dataRool,
  editPasswordOnly
}) => {
  const apiUrl = config.apiBaseUrl;

  // contenedor del área de contenido (para confinar modal y drawer)
  const contentContainer =
    typeof window !== 'undefined'
      ? document.getElementById('content-root')
      : undefined;

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // catálogos por empresa
  const [catalogs, setCatalogs] = useState({});       // { [eid]: { centros: [...], departamentos: [...] } }
  const [loadingCats, setLoadingCats] = useState({}); // { [eid]: boolean }

  // Modal Selector State (Opción 2)
  const [modalSelector, setModalSelector] = useState({ open: false, eid: null, key: null, title: '' });

  const openModalSelector = (eid, key, empresaNombre) => {
    setModalSelector({
      open: true,
      eid,
      key,
      title: `${empresaNombre} • ${key === 'centros' ? 'Centros de costo' : 'Departamentos'}`
    });
  };
  const closeModalSelector = () => setModalSelector({ open: false, eid: null, key: null, title: '' });

  // helpers de campos
  const onField = (name, value) => {
    handleInputChange(name, value);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };
  const handleChange = (e) => onField(e.target.name, e.target.value);

const handleEmpresaChange = (_, newValue) => {
  const selectedIds = (newValue || []).map((emp) => emp.EMPRESAID);
  onField('empresas', selectedIds);

  // Tomamos lo que ya teníamos
  const prevAccesos = formData.accesos || {};
  const nextAccesos = { ...prevAccesos };

  // Para las nuevas empresas seleccionadas, aseguramos objeto vacío
  selectedIds.forEach((id) => {
    if (!nextAccesos[id]) {
      nextAccesos[id] = { centros: [], departamentos: [] };
    }
  });

  // 👀 IMPORTANTE:
  // Ya NO borramos accesos de empresas desmarcadas.
  // Se quedan guardados por si después el usuario vuelve a marcar esa empresa.

  onField('accesos', nextAccesos);

  if (errors.empresas) {
    setErrors((prev) => ({ ...prev, empresas: '' }));
  }
};

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleCancel = () => { setErrors({}); handleClose(); };

  const validateFields = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!editPasswordOnly) {
      if (!formData.USERNAME?.trim()) newErrors.USERNAME = 'El nombre de usuario no puede estar vacío';
      if (!formData.CORREO?.trim()) newErrors.CORREO = 'El correo no puede estar vacío';
      else if (!emailRegex.test(formData.CORREO)) newErrors.CORREO = 'El formato del correo no es válido';
      if (!formData.empresas?.length) newErrors.empresas = 'Debe seleccionar al menos una empresa';
      if (!formData.TIPOUSER) newErrors.TIPOUSER = 'Debe seleccionar un tipo de usuario';
      
      const permitePass = formData.permitir_password !== undefined ? Number(formData.permitir_password) === 1 : true;
      const permiteMs = formData.permitir_microsoft !== undefined ? Number(formData.permitir_microsoft) === 1 : true;

      if (!permitePass && !permiteMs) {
        newErrors.metodos = 'Debe habilitar al menos un método de inicio de sesión (Contraseña o Microsoft SSO)';
      }

      if (permitePass && !formData.USUARIOID && !formData.PASSWORD?.trim()) {
        newErrors.PASSWORD = 'La contraseña es obligatoria para acceso local';
      }
    } else {
      if (!formData.PASSWORD?.trim()) newErrors.PASSWORD = 'La contraseña es obligatoria';
    }
    return newErrors;
  };

  const handleSubmit = () => {
    const v = validateFields();
    if (Object.keys(v).length) { setErrors(v); return; }
    handleSave();
    setErrors({});
  };

  // empresas visibles
  const empresasSeleccionadas = useMemo(
    () => (dataEmpresa || []).filter((e) => (formData.empresas || []).includes(e.EMPRESAID)),
    [dataEmpresa, formData.empresas]
  );

  // fetch batch de catálogos faltantes
  useEffect(() => {
    const faltantes = (formData.empresas || []).filter((eid) => !catalogs[eid]);
    if (faltantes.length === 0) return;

    setLoadingCats((prev) => {
      const nx = { ...prev }; faltantes.forEach((eid) => { nx[eid] = true; }); return nx;
    });

    const cargarBatch = async () => {
      try {
        const { data } = await axios.post(`${apiUrl}/catalogos/centrosDepartamentos`, { empresaIds: faltantes });
        setCatalogs((prev) => ({ ...prev, ...(data || {}) }));
      } catch (e) {
        console.error('Error cargando catálogos:', e);
      } finally {
        setLoadingCats((prev) => {
          const nx = { ...prev }; faltantes.forEach((eid) => { nx[eid] = false; }); return nx;
        });
      }
    };
    cargarBatch();
  }, [apiUrl, formData.empresas, catalogs]);

  // helpers de selección
  const upsertAccesos = (empresaId, key, ids) => {
    const next = { ...(formData.accesos || {}) };
    next[empresaId] = {
      centros: key === 'centros' ? ids : (next[empresaId]?.centros || []),
      departamentos: key === 'departamentos' ? ids : (next[empresaId]?.departamentos || []),
    };
    onField('accesos', next);
    const errKey = `accesos_${empresaId}`;
    if (errors[errKey]) setErrors((prev) => ({ ...prev, [errKey]: '' }));
  };

  // util para chips de resumen (muestra 3 y agrega "+x más")
  const ChipsResumen = ({ items = [], selectedIds = [] }) => {
    const selected = items.filter(i => selectedIds.includes(i.id));
    const primeros = selected.slice(0, 3);
    const restantes = selected.length - primeros.length;

    return (
      <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
        {primeros.map((i) => (
          <Chip
            key={i.id}
            label={i.nombre}
            size="small"
            sx={{
              fontSize: '0.68rem',
              height: 20,
              bgcolor: '#e0f2fe',
              color: '#0369a1',
              fontWeight: 600,
              border: '1px solid #bae6fd'
            }}
          />
        ))}
        {restantes > 0 && (
          <Chip
            label={`+${restantes} más`}
            size="small"
            sx={{
              fontSize: '0.68rem',
              height: 20,
              bgcolor: '#f1f5f9',
              color: '#475569',
              fontWeight: 700
            }}
          />
        )}
      </Stack>
    );
  };

  // Estado para la pestaña de empresa activa
  const [activeTab, setActiveTab] = useState(0);

  // Asegurar que activeTab sea válido cuando cambian las empresas seleccionadas
  useEffect(() => {
    if (activeTab >= empresasSeleccionadas.length && empresasSeleccionadas.length > 0) {
      setActiveTab(0);
    }
  }, [empresasSeleccionadas, activeTab]);

  return (
    <Dialog
      open={isOpen}
      disableRestoreFocus
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      container={contentContainer}
      disableEnforceFocus
      PaperProps={{
        sx: {
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.14)',
          border: '1px solid rgba(226,232,240,0.8)',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header Premium con Clamp */}
      <Box
        sx={{
          px: 'clamp(12px, 1.5vw, 20px)',
          py: 'clamp(8px, 1vw, 12px)',
          background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
        }}
      >
        <Avatar
          sx={{
            width: 'clamp(28px, 2.5vw, 34px)',
            height: 'clamp(28px, 2.5vw, 34px)',
            bgcolor: 'rgba(255,255,255,0.18)',
            color: 'white',
            backdropFilter: 'blur(4px)',
            borderRadius: '9px'
          }}
        >
          <PersonRoundedIcon sx={{ fontSize: 'clamp(16px, 1.2vw, 20px)' }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 'clamp(0.88rem, 0.84rem + 0.2vw, 1.05rem)', fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
            {editPasswordOnly ? 'Editar Contraseña' : 'Crear / Editar Usuario'}
          </Typography>
          {!editPasswordOnly && (
            <Typography variant="caption" sx={{ opacity: 0.88, fontSize: 'clamp(0.66rem, 0.62rem + 0.05vw, 0.74rem)', display: 'block', mt: 0.2 }}>
              Completa la información del usuario y asigna los accesos correspondientes por empresa
            </Typography>
          )}
        </Box>
        <Tooltip title="Cerrar">
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: 'white',
              p: '4px',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <CloseIcon fontSize="small" sx={{ fontSize: 'clamp(15px, 1.1vw, 18px)' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 'clamp(10px, 1.4vw, 18px)', bgcolor: '#f8fafc' }}>
        <Stack spacing={1.2}>
          {!editPasswordOnly && (
            <>
              {/* Datos del usuario */}
              <SectionCard icon={<PersonRoundedIcon />} title="Datos del usuario" subtitle="Información general de la cuenta">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                  <TextField
                    autoFocus
                    size="small"
                    label="Nombre de usuario"
                    name="USERNAME"
                    value={formData.USERNAME || ''}
                    onChange={handleChange}
                    fullWidth
                    error={Boolean(errors.USERNAME)}
                    helperText={errors.USERNAME}
                    sx={elegantInputSx}
                  />
                  <TextField
                    size="small"
                    label="Correo electrónico"
                    name="CORREO"
                    type="email"
                    value={formData.CORREO || ''}
                    onChange={handleChange}
                    fullWidth
                    error={Boolean(errors.CORREO)}
                    helperText={errors.CORREO}
                    sx={elegantInputSx}
                  />
                  <TextField
                    size="small"
                    label="Nombre(s) en ERP (comprador)"
                    name="COMPRADOR_ERP_VARIANTS"
                    value={formData.COMPRADOR_ERP_VARIANTS || ''}
                    onChange={handleChange}
                    fullWidth
                    placeholder="Ej: KARELI ESPINOZA, ..."
                    helperText="Variantes separadas por coma."
                    sx={elegantInputSx}
                  />
                </Stack>
              </SectionCard>

              {/* Accesos */}
              <SectionCard icon={<BusinessRoundedIcon />} title="Accesos y Roles" subtitle="Empresas asignadas y nivel de permisos">
                <Stack spacing={1.2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                    {/* Empresas */}
                    <FormControl fullWidth error={Boolean(errors.empresas)}>
                      <Autocomplete
                        multiple
                        options={dataEmpresa || []}
                        getOptionLabel={(opt) => opt.NOMBRE_EMPRESA || ''}
                        value={(dataEmpresa || []).filter((emp) => (formData.empresas || []).includes(emp.EMPRESAID))}
                        onChange={handleEmpresaChange}
                        isOptionEqualToValue={(option, value) => option.EMPRESAID === value.EMPRESAID}
                        filterSelectedOptions
                        limitTags={2}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={option.EMPRESAID}
                              label={option.NOMBRE_EMPRESA}
                              {...getTagProps({ index })}
                              size="small"
                              sx={{ borderRadius: '5px', fontWeight: 600, fontSize: '0.7rem', height: 20 }}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Seleccionar Empresa(s)"
                            placeholder="Empresas"
                            error={Boolean(errors.empresas)}
                            helperText={errors.empresas}
                            sx={elegantInputSx}
                          />
                        )}
                      />
                    </FormControl>

                    {/* Tipo de usuario */}
                    <FormControl fullWidth error={Boolean(errors.TIPOUSER)}>
                      <InputLabel id="tipo-user-label" sx={{ fontSize: '0.8rem' }}>Tipo de usuario</InputLabel>
                      <Select
                        size="small"
                        labelId="tipo-user-label"
                        name="TIPOUSER"
                        value={formData.TIPOUSER || ''}
                        onChange={handleChange}
                        label="Tipo de usuario"
                        sx={elegantInputSx}
                        MenuProps={{ disableRestoreFocus: true }}
                      >
                        {(dataRool || []).map((role) => (
                          <MenuItem key={role.TIPOUSERID} value={role.TIPOUSERID} sx={{ fontSize: '0.8rem' }}>
                            {role.TIPO_USER}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.TIPOUSER && <FormHelperText>{errors.TIPOUSER}</FormHelperText>}
                    </FormControl>
                  </Stack>

                  {/* Resumen por empresa con Botones que abren SelectorModal Centrado */}
                  {empresasSeleccionadas.length > 0 && (
                    <Box sx={{ pt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.66rem', display: 'block', mb: 0.4 }}>
                        Configuración detallada de Centros y Departamentos
                      </Typography>
                      <Box sx={{ bgcolor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '7px', p: '6px 10px', mb: 1, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Typography variant="caption" sx={{ color: '#0369a1', fontSize: '0.7rem', fontWeight: 500 }}>
                          ℹ️ <strong>Regla de acceso:</strong> Si "Todos" está marcado, el usuario podrá ver el 100% de los registros de esa categoría. Para restringir el acceso a centros o departamentos específicos, haz clic en <strong>"Añadir..."</strong>.
                        </Typography>
                      </Box>
                      <Stack spacing={1}>
                        {empresasSeleccionadas.map((emp) => {
                          const eid = emp.EMPRESAID;
                          const cat = catalogs[eid];
                          const isLoading = !!loadingCats[eid];
                          const seleccion = formData.accesos?.[eid] || { centros: [], departamentos: [] };

                          const totalCentros = cat?.centros?.length || 0;
                          const totalDeps = cat?.departamentos?.length || 0;
                          const selCentros = seleccion.centros?.length || 0;
                          const selDeps = seleccion.departamentos?.length || 0;

                          // Si selCentros === 0 o selCentros === totalCentros → Significa "Acceso a Todos"
                          const allCentrosChecked = selCentros === 0 || (totalCentros > 0 && selCentros === totalCentros);
                          const allDepsChecked = selDeps === 0 || (totalDeps > 0 && selDeps === totalDeps);

                          return (
                            <Paper
                              key={eid}
                              elevation={0}
                              sx={{
                                p: 'clamp(8px, 1vw, 12px)',
                                borderRadius: '10px',
                                border: '1px solid #e2e8f0',
                                backgroundColor: '#ffffff',
                                '&:hover': { borderColor: '#cbd5e1' }
                              }}
                            >
                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ pb: 0.8, borderBottom: '1px solid #e2e8f0' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: 'clamp(0.76rem, 0.74rem + 0.06vw, 0.84rem)' }}>
                                  {emp.NOMBRE_EMPRESA}
                                </Typography>

                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={allCentrosChecked}
                                        onChange={() => {
                                          upsertAccesos(eid, 'centros', []);
                                        }}
                                        disabled={isLoading}
                                        sx={{ p: 0.3, color: '#94a3b8', '&.Mui-checked': { color: '#0655A5' } }}
                                      />
                                    }
                                    label={<Typography variant="body2" sx={{ fontSize: '0.74rem', fontWeight: 600, color: '#334155' }}>Todos centros</Typography>}
                                  />

                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={allDepsChecked}
                                        onChange={() => {
                                          upsertAccesos(eid, 'departamentos', []);
                                        }}
                                        disabled={isLoading}
                                        sx={{ p: 0.3, color: '#94a3b8', '&.Mui-checked': { color: '#0655A5' } }}
                                      />
                                    }
                                    label={<Typography variant="body2" sx={{ fontSize: '0.74rem', fontWeight: 600, color: '#334155' }}>Todos deptos</Typography>}
                                  />

                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon sx={{ fontSize: '0.75rem !important' }} />}
                                    onClick={() => openModalSelector(eid, 'centros', emp.NOMBRE_EMPRESA)}
                                    disabled={isLoading || totalCentros === 0}
                                    sx={{
                                      fontSize: '0.7rem',
                                      fontWeight: 700,
                                      borderRadius: '6px',
                                      borderColor: '#cbd5e1',
                                      color: '#0655A5',
                                      textTransform: 'none',
                                      py: 0.2,
                                      px: 1
                                    }}
                                  >
                                    {selCentros > 0 ? `Centros (${selCentros}/${totalCentros})` : 'Añadir centros'}
                                  </Button>

                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon sx={{ fontSize: '0.75rem !important' }} />}
                                    onClick={() => openModalSelector(eid, 'departamentos', emp.NOMBRE_EMPRESA)}
                                    disabled={isLoading || totalDeps === 0}
                                    sx={{
                                      fontSize: '0.7rem',
                                      fontWeight: 700,
                                      borderRadius: '6px',
                                      borderColor: '#cbd5e1',
                                      color: '#0655A5',
                                      textTransform: 'none',
                                      py: 0.2,
                                      px: 1
                                    }}
                                  >
                                    {selDeps > 0 ? `Deptos (${selDeps}/${totalDeps})` : 'Añadir deptos'}
                                  </Button>
                                </Stack>
                              </Stack>

                              {/* Resumen de selección en chips */}
                              <Box sx={{ pt: 0.8 }}>
                                <Grid container spacing={1}>
                                  <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem' }}>
                                      Centros de Costo seleccionados:
                                    </Typography>
                                    {(seleccion.centros || []).length === 0 ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                                        <Chip
                                          label="Acceso a TODOS los centros"
                                          size="small"
                                          sx={{
                                            bgcolor: '#ecfdf5',
                                            color: '#047857',
                                            border: '1px solid #a7f3d0',
                                            fontWeight: 700,
                                            fontSize: '0.68rem',
                                            height: '20px',
                                          }}
                                        />
                                      </Box>
                                    ) : (
                                      <ChipsResumen items={cat?.centros || []} selectedIds={seleccion.centros || []} />
                                    )}
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem' }}>
                                      Departamentos seleccionados:
                                    </Typography>
                                    {(seleccion.departamentos || []).length === 0 ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                                        <Chip
                                          label="Acceso a TODOS los departamentos"
                                          size="small"
                                          sx={{
                                            bgcolor: '#ecfdf5',
                                            color: '#047857',
                                            border: '1px solid #a7f3d0',
                                            fontWeight: 700,
                                            fontSize: '0.68rem',
                                            height: '20px',
                                          }}
                                        />
                                      </Box>
                                    ) : (
                                      <ChipsResumen items={cat?.departamentos || []} selectedIds={seleccion.departamentos || []} />
                                    )}
                                  </Grid>
                                </Grid>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </SectionCard>
            </>
          )}

          {/* Credenciales y Métodos de Autenticación */}
          <SectionCard icon={<LockRoundedIcon />} title="Credenciales y Métodos de Acceso" subtitle="Configuración de inicio de sesión y seguridad (ISO 27001)">
            <Stack spacing={1.5}>
              <TextField
                size="small"
                label="Contraseña"
                name="PASSWORD"
                type={showPassword ? 'text' : 'password'}
                value={formData.PASSWORD || ''}
                onChange={handleChange}
                fullWidth
                error={Boolean(errors.PASSWORD)}
                helperText={errors.PASSWORD || (editPasswordOnly ? 'Solo actualizará la contraseña del usuario' : '')}
                sx={elegantInputSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword} edge="end" aria-label="Mostrar/Ocultar contraseña" size="small">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              {!editPasswordOnly && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.68rem', mt: 0.5 }}>
                    Métodos de Inicio de Sesión Permitidos
                  </Typography>

                  {errors.metodos && (
                    <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 700, fontSize: '0.72rem' }}>
                      ⚠️ {errors.metodos}
                    </Typography>
                  )}

                  {/* 1. Acceso con Contraseña Local */}
                  <Box sx={{ bgcolor: '#f8fafc', p: 1.2, borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={formData.permitir_password !== undefined ? Number(formData.permitir_password) === 1 : true}
                          onChange={(e) => onField('permitir_password', e.target.checked ? 1 : 0)}
                          sx={{ color: '#0655A5', '&.Mui-checked': { color: '#0655A5' } }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>
                            🔑 Acceso con Contraseña Local (Normal)
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#64748b', display: 'block' }}>
                            Permite al usuario iniciar sesión ingresando su usuario y contraseña institucional.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* 2. Exigir 2FA por Correo */}
                  <Box sx={{ bgcolor: '#f8fafc', p: 1.2, borderRadius: '8px', border: '1px solid #e2e8f0', ml: { sm: 2 } }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Number(formData.requiere_2fa) === 1}
                          onChange={(e) => onField('requiere_2fa', e.target.checked ? 1 : 0)}
                          sx={{ color: '#0655A5', '&.Mui-checked': { color: '#0655A5' } }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>
                            🛡️ Exigir Código 2FA al Correo (Doble Factor)
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#64748b', display: 'block' }}>
                            Al ingresar con contraseña, se enviará un código de verificación de 6 dígitos a su correo institucional.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* 3. Acceso con Microsoft Office 365 */}
                  <Box sx={{ bgcolor: '#f8fafc', p: 1.2, borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={formData.permitir_microsoft !== undefined ? Number(formData.permitir_microsoft) === 1 : true}
                          onChange={(e) => onField('permitir_microsoft', e.target.checked ? 1 : 0)}
                          sx={{ color: '#0655A5', '&.Mui-checked': { color: '#0655A5' } }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>
                            🏢 Acceso con Microsoft Office 365 (SSO)
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#64748b', display: 'block' }}>
                            Permite iniciar sesión con un clic usando su cuenta oficial de Microsoft (@eseasaoffshore.com o @grupoeseasa.com).
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Box>
              )}
            </Stack>
          </SectionCard>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 'clamp(12px, 1.5vw, 20px)', py: 'clamp(8px, 1vw, 12px)', bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleCancel}
          sx={{
            borderRadius: '7px',
            px: 'clamp(10px, 1.2vw, 16px)',
            py: 'clamp(4px, 0.5vw, 6px)',
            fontWeight: 700,
            fontSize: 'clamp(0.72rem, 0.68rem + 0.06vw, 0.78rem)',
            textTransform: 'none',
            color: '#64748b',
            borderColor: '#cbd5e1',
            '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' }
          }}
        >
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSubmit}
          sx={{
            borderRadius: '7px',
            px: 'clamp(14px, 1.5vw, 20px)',
            py: 'clamp(5px, 0.6vw, 7px)',
            fontWeight: 800,
            fontSize: 'clamp(0.72rem, 0.68rem + 0.08vw, 0.82rem)',
            textTransform: 'none',
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            boxShadow: '0 3px 10px rgba(6,85,165,0.22)',
            '&:hover': {
              background: 'linear-gradient(135deg, #044384 0%, #0655A5 100%)',
              boxShadow: '0 5px 14px rgba(6,85,165,0.32)'
            }
          }}
        >
          Guardar Cambios
        </Button>
      </DialogActions>

      {/* Modal Selector Centrado (Opción 2) */}
      <SelectorModal
        open={modalSelector.open}
        onClose={closeModalSelector}
        title={modalSelector.title}
        options={modalSelector.eid && modalSelector.key ? (catalogs[modalSelector.eid]?.[modalSelector.key] || []) : []}
        selectedIds={
          modalSelector.eid && modalSelector.key
            ? (formData.accesos?.[modalSelector.eid]?.[modalSelector.key] || [])
            : []
        }
        onChange={(ids) => {
          if (!modalSelector.eid || !modalSelector.key) return;
          upsertAccesos(modalSelector.eid, modalSelector.key, ids);
        }}
        container={contentContainer}
        isCentros={modalSelector.key === 'centros'}
      />
    </Dialog>
  );
};

export default ModalUsuarios;